import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { CheckIcon } from "@radix-ui/react-icons";
import * as React from "react";

import { cn } from "../../utils";

import { Check } from "lucide-react";
import { PropsWithChildren } from "react";

export interface CheckboxShellProps {
  className?: string;
  disabled?: boolean;
  id?: string;
  checked: boolean;
  onCheckedChange?: (checked: boolean) => void;
  onClick?: (e: React.MouseEvent) => void;
  ref?: React.RefObject<HTMLButtonElement>;
}

export function CheckboxShell(props: PropsWithChildren<CheckboxShellProps>) {
  const { checked, onCheckedChange, id, className, disabled, onClick, ref } =
    props;

  return (
    <button
      ref={ref}
      type="button"
      tabIndex={0}
      id={id}
      className={cn(
        "focus-visible:ring-ring peer flex size-5 shrink-0 place-content-center items-center justify-center overflow-hidden rounded-[6px] border border-gray-300 bg-white text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed",
        {
          "bg-primary-blue border-primary-blue text-white": checked,
          "border-gray-300 bg-gray-100 text-gray-500 opacity-50": disabled,
          "bg-gray-200": checked && disabled,
        },
        className
      )}
      aria-disabled={disabled}
      onClick={(e) => {
        if (disabled) return;
        if (onCheckedChange) {
          e.stopPropagation();
          onCheckedChange(!checked);
        }
        onClick?.(e);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.stopPropagation();
          onCheckedChange?.(!checked);
        }
      }}
    >
      {checked && <Check className={cn("size-full bg-inherit")} />}
    </button>
  );
}

export type CheckboxProps = React.ComponentProps<typeof CheckboxPrimitive.Root>;

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> & {
    className?: string;
  }
>(({ className, ...props }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    className={cn(
      "border-primary focus-visible:ring-ring data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground peer h-4 w-4 shrink-0 rounded-sm border shadow focus-visible:outline-none focus-visible:ring-1 disabled:cursor-not-allowed disabled:opacity-50",
      className
    )}
    {...props}
  >
    <CheckboxPrimitive.Indicator
      className={cn("flex items-center justify-center text-current")}
    >
      <CheckIcon className="size-4" />
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
));
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox };
